import { useQuery } from '@apollo/client'
import { useState, useEffect, FC } from 'react'
import { Flex, Box, Text, Heading, Card } from 'rebass/styled-components'
import styled from 'styled-components'
import { GET_PATIENT_MEMBERSHIPS_TAGS } from '../../../graphql/queries'
import { IGethealthCoachPatientProfileMembershipsTagsOutput, IGethealthCoachPatientProfileMembershipsTagsInput, IGethealthCoachPatientProfileMembershipsTag, ISubscriptionCard } from '../../../graphql/types/queries/patient'
import Modal from 'styled-react-modal'
import { SpecialModalBackground } from '../../../components/Commons'
import moment from 'moment'
import { Icon } from '@iconify/react';
import visaIcon from '@iconify/icons-logos/visa';
import mcIcon from '@iconify/icons-logos/mastercard';
import amexIcon from '@iconify/icons-logos/amex';
import discoverIcon from '@iconify/icons-logos/discover';
import creditCard from '@iconify/icons-entypo/credit-card';
import { Tabulator } from './Tabulator'
import { HoverableCard } from '../../../components/HoverableCard'

const Tag = styled(Flex)`
  min-width: 28px;
  height: 28px;
  margin-right: 12px;
  padding-left: 3px ;
  padding-right: 3px ;
  justify-content: center;
  align-items: center;
`
const Wrapper = styled(Flex)`
  overflow: auto;
  width: 800px;
  padding-right: 20px;
  margin-right: 20px;
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #6c89b7;
    border-radius: 5px;
  }
  &::-webkit-scrollbar {
    width: 7px;
    border: 1px solid transparent;
  }
`;
const TagWrap = styled(Flex)`
  padding: 2px 8px;
  align-items: center;
  border-radius: 2px;
  overflow: hidden;
  position: relative;
`

interface IActiveTagProps {
  active?: boolean,
  text: string
}

const UseCurrentCard: React.FC<{
  card: ISubscriptionCard
}> = ({ card }) => {
  const last4 = (card || {}).last4;
  const cardBrand = (card || {}).brand;
  let logo = creditCard;
  switch ((cardBrand || '').toUpperCase()) {
    case 'VISA':
      logo = visaIcon;
      break;
    case 'MC':
    case 'MASTERCARD':
    case 'MASTER CARD':
      logo = mcIcon;
      break;
    case 'AX':
    case 'AMEX':
    case 'AMERICANEXPRESS':
    case 'AMERICAN EXPRESS':
      logo = amexIcon;
      break;
    case 'DISCO':
    case 'DISCOVER':
      logo = discoverIcon;
      break;

    default:
      logo = creditCard;
      break;
  }

  return (
    <Flex flexDirection="row" alignItems="center">
      <Box
        p={2}
        mr={2}
        style={{
          border: '1px solid rgba(0, 0, 0, 0.1)',
          borderRadius: '5px',
          display: 'flex',
        }}
      >
        <Icon icon={logo} />
      </Box>

      <Flex flexDirection="column" flex={1}>
        <Text fontWeight="600" fontSize="14px">{`•••• ${last4}`}</Text>
        <Text fontWeight="300" fontSize="14px">{`Expires ${card.exp_month}/${card.exp_year}`}</Text>
      </Flex>
    </Flex>
  );
};

export const ActiveTag: React.FC<IActiveTagProps> = ({ active, text }) => {
  return (
    <TagWrap mr={2} style={{
      backgroundColor: active ? 'rgb(215,247,194)' : 'rgb(235,238,241)'
    }}>
      <Text style={{ zIndex: 1 }} color={active ? 'rgb(0,105,8)' : 'rgb(84,89,105)'} fontSize={'13px'}>{text}</Text>
    </TagWrap>);
}

interface IMembershipTagsProps {
  athenaId: string
}

const TagWrapper: FC<{
  isActive: boolean,
  text: string,
  color: string,
  onClick: () => void,
  forceText?: string
}> = ({ isActive, text, color, onClick, forceText }) => {
  return (
    <Tag onClick={onClick} style={{ background: color, opacity: isActive ? 1 : 0.23, cursor: isActive ? 'pointer' : 'not-allowed' }}>
      <Text color={'white'}>{forceText || text.substring(0, 1)}</Text>
    </Tag>
  )
}

export const MembershipTags: React.FC<IMembershipTagsProps> = ({ athenaId }) => {
  const [selectedTab, setSelectedTab] = useState<'CreditCard' | 'Receipts'>('CreditCard')
  const [viewMembershipDetail, setViewMembershipDetail] = useState<IGethealthCoachPatientProfileMembershipsTag & {
    target: 'Membership' | 'Vitamins Subscription' | 'Kits Subscription' | 'GI subscription'
  }>()
  const { data, error, loading } = useQuery<IGethealthCoachPatientProfileMembershipsTagsOutput, IGethealthCoachPatientProfileMembershipsTagsInput>(GET_PATIENT_MEMBERSHIPS_TAGS, {
    variables: {
      athenaId
    }
  });

  if (!!loading) {
    return <Text>Loading memberships</Text>
  }

  if (!data || !data.gethealthCoachPatientProfileMembershipsTags) {
    return null
  }


  const membershipData = data.gethealthCoachPatientProfileMembershipsTags.memberships.find(
    x => x.subscriptionTarget === 'SUBSCRIPTION_MEMBERSHIP' &&
      x.subscriptionNickname !== 'Complete Health Boost (everything+health coaching)' &&
      x.subscriptionNickname !== 'Metabolic Reset (thyroid, menopause and weight)' &&
      x.subscriptionNickname !== 'Weight Loss'
  );
  const kitData = data.gethealthCoachPatientProfileMembershipsTags.memberships.find(
    x => x.subscriptionTarget === 'SUBSCRIPTION_KIT'
  );
  const vitaminsData = data.gethealthCoachPatientProfileMembershipsTags.memberships.find(
    x => x.subscriptionTarget === 'SUBSCRIPTION_VITAMINS'
  );
  const membershipCompleteHealthBoostData = data.gethealthCoachPatientProfileMembershipsTags.memberships.find(
    x => x.subscriptionTarget === 'SUBSCRIPTION_MEMBERSHIP' &&
      x.subscriptionNickname === 'Complete Health Boost (everything+health coaching)'
  );
  const membershipMetabolicResetData = data.gethealthCoachPatientProfileMembershipsTags.memberships.find(
    x => x.subscriptionTarget === 'SUBSCRIPTION_MEMBERSHIP' &&
      x.subscriptionNickname === 'Metabolic Reset (thyroid, menopause and weight)'
  );
  const membershipWeightlossResetData = data.gethealthCoachPatientProfileMembershipsTags.memberships.find(
    x => x.subscriptionTarget === 'SUBSCRIPTION_MEMBERSHIP' &&
      x.subscriptionNickname === 'Weight Loss'
  );
  const membershipGiData = data.gethealthCoachPatientProfileMembershipsTags.memberships.find(
    x => x.subscriptionTarget === 'SUBSCRIPTION_GI'
  );

  return (
    <Flex alignItems={'center'}>
      {/* <Text mr={4}>Subscriptions: </Text> */}
      {(!!membershipData || (!membershipWeightlossResetData && !membershipMetabolicResetData && !membershipCompleteHealthBoostData)) && (
        <TagWrapper onClick={() => {
          if (!membershipData) return
          setViewMembershipDetail({
            ...membershipData,
            target: 'Membership'
          }
          )
        }} isActive={!!membershipData} color='#8bbdae' text={'Member'} forceText={!!membershipData ? membershipData.isInsuranceMembership ? 'IM' : 'SM' : 'M'} />
      )}

      {!!membershipWeightlossResetData && (
        <TagWrapper onClick={() => {
          if (!membershipWeightlossResetData) return
          setViewMembershipDetail({
            ...membershipWeightlossResetData,
            target: 'Membership'
          }
          )
        }} isActive={!!membershipWeightlossResetData} color='#8bbdae' text={'Member'} forceText={'WL'} />
      )}

      {!!membershipMetabolicResetData && (
        <TagWrapper onClick={() => {
          if (!membershipMetabolicResetData) return
          setViewMembershipDetail({
            ...membershipMetabolicResetData,
            target: 'Membership'
          }
          )
        }} isActive={!!membershipMetabolicResetData} color='#8bbdae' text={'Member'} forceText={'MR'} />
      )}

      {!!membershipCompleteHealthBoostData && (
        <TagWrapper onClick={() => {
          if (!membershipCompleteHealthBoostData) return
          setViewMembershipDetail({
            ...membershipCompleteHealthBoostData,
            target: 'Membership'
          }
          )
        }} isActive={!!membershipCompleteHealthBoostData} color='#8bbdae' text={'Member'} forceText={'CHB'} />
      )}

      <TagWrapper onClick={() => {
        if (!vitaminsData) return
        setViewMembershipDetail({
          ...vitaminsData,
          target: 'Vitamins Subscription'
        }
        )
      }} isActive={!!vitaminsData} color='#FFABA0' text={'Vitamins'} />
      <TagWrapper onClick={() => {
        if (!kitData) return
        setViewMembershipDetail({
          ...kitData,
          target: 'Kits Subscription'
        }
        )
      }} isActive={!!kitData} color='#344F7A' text={'Kits'} />

      {!!membershipGiData && (
        <TagWrapper onClick={() => {
          if (!membershipGiData) return
          setViewMembershipDetail({
            ...membershipGiData,
            target: 'GI subscription'
          }
          )
        }} isActive={!!membershipGiData} color='#344F7A' text={'Gut Reset'} />
      )}

      {!!viewMembershipDetail && (
        <Modal
          isOpen={true}
          onBackgroundClick={() => {
            setViewMembershipDetail(undefined);
          }}
          onEscapeKeydown={() => {
            setViewMembershipDetail(undefined);
          }}
        >
          <SpecialModalBackground
            onClick={() => {
              setViewMembershipDetail(undefined);
            }}
          >
            <Box
              style={{
                margin: '0 auto',
                alignSelf: 'center',
                borderRadius: '20px',
                position: 'relative',
                background: 'linear-gradient(180deg, #BBD4ED 0%, #FDF5F2 100%)',
                padding: '24px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                maxWidth: '95vw',
                minWidth: 'initial',
                maxHeight: '95vh',
                minHeight: '95vh',
                overflowY: 'hidden',
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <Wrapper flex={1} flexDirection={'column'} width={'800px'} style={{ overflowY: 'auto' }}>
                <Flex alignItems={'center'}>
                  <Heading flex={1} fontSize={'24px'}>Membership: {viewMembershipDetail.subscriptionNickname}</Heading>
                  <ActiveTag text={
                    viewMembershipDetail.isPaused ?
                      viewMembershipDetail.pausedSubscription ?
                        `Paused until ${moment((viewMembershipDetail.pausedSubscription.resumesAt || 0) * 1000).format('MM/DD/YYYY')}`
                        : 'Paused indefinitely'
                      : viewMembershipDetail.cancels
                        ? `Cancels on ${viewMembershipDetail.cancels.cancelAtPeriodEnd
                          ? 'end of period'
                          : viewMembershipDetail.cancels.cancelsAt
                            ? moment(viewMembershipDetail.cancels.cancelsAt * 1000).format('MM/DD/YYYY')
                            : 'unknown'}` :
                        'Active'}
                    active={
                      !viewMembershipDetail.isPaused && (!viewMembershipDetail.pausedSubscription?.resumesAt) && (!viewMembershipDetail.cancels || (!viewMembershipDetail.cancels?.cancelAtPeriodEnd && !viewMembershipDetail.cancels?.cancelsAt))
                    }
                  />
                  <Icon icon={'fa6-brands:cc-stripe'} fontSize={22} style={{ cursor: 'pointer' }} onClick={() => {
                    window.open(`https://dashboard.stripe.com/subscriptions/${viewMembershipDetail.id}`, '_blank')
                  }} />
                </Flex>
                <Flex mt={4} style={{ padding: '12px 20px' }} mb={4}>
                  <Flex mr={2} justifyItems={'center'} alignItems={'baseline'}>
                    <Text fontSize={'12px'}>Price per period</Text>
                    <Text ml={2} fontWeight={600}>${(viewMembershipDetail.price || 0) / 100}</Text>
                  </Flex>
                  <Flex flex={1} alignItems={'baseline'} justifyContent={'flex-end'}>
                    {viewMembershipDetail.nextBill && (
                      <Flex alignItems={'baseline'}>
                        <Text fontSize={'12px'}>Next Bill</Text>
                        <Text ml={2} fontWeight={600}>${(viewMembershipDetail.nextBill?.amount || 0) / 100}</Text>
                        {viewMembershipDetail.nextBill.date && (
                          <>
                            <Text ml={3} fontSize={'12px'}>On</Text>
                            <Text ml={2} fontWeight={600}>{moment(viewMembershipDetail.nextBill.date * 1000).format('MM/DD/YYYY')}</Text>
                          </>
                        )}
                      </Flex>
                    )}
                  </Flex>
                </Flex>

                <Tabulator
                  selectedTab={selectedTab}
                  onTabChanged={(e) => {
                    setSelectedTab(e as any)
                  }}
                  tabs={[{
                    icon: (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 6C0 5.20435 0.316071 4.44129 0.87868 3.87868C1.44129 3.31607 2.20435 3 3 3H21C21.7956 3 22.5587 3.31607 23.1213 3.87868C23.6839 4.44129 24 5.20435 24 6V18C24 18.7956 23.6839 19.5587 23.1213 20.1213C22.5587 20.6839 21.7956 21 21 21H3C2.20435 21 1.44129 20.6839 0.87868 20.1213C0.316071 19.5587 0 18.7956 0 18V6ZM3.75 7.5C3.55109 7.5 3.36032 7.57902 3.21967 7.71967C3.07902 7.86032 3 8.05109 3 8.25V9.75C3 9.94891 3.07902 10.1397 3.21967 10.2803C3.36032 10.421 3.55109 10.5 3.75 10.5H6.75C6.94891 10.5 7.13968 10.421 7.28033 10.2803C7.42098 10.1397 7.5 9.94891 7.5 9.75V8.25C7.5 8.05109 7.42098 7.86032 7.28033 7.71967C7.13968 7.57902 6.94891 7.5 6.75 7.5H3.75ZM3.75 12C3.55109 12 3.36032 12.079 3.21967 12.2197C3.07902 12.3603 3 12.5511 3 12.75C3 12.9489 3.07902 13.1397 3.21967 13.2803C3.36032 13.421 3.55109 13.5 3.75 13.5H11.25C11.4489 13.5 11.6397 13.421 11.7803 13.2803C11.921 13.1397 12 12.9489 12 12.75C12 12.5511 11.921 12.3603 11.7803 12.2197C11.6397 12.079 11.4489 12 11.25 12H3.75ZM3.75 15C3.55109 15 3.36032 15.079 3.21967 15.2197C3.07902 15.3603 3 15.5511 3 15.75C3 15.9489 3.07902 16.1397 3.21967 16.2803C3.36032 16.421 3.55109 16.5 3.75 16.5H5.25C5.44891 16.5 5.63968 16.421 5.78033 16.2803C5.92098 16.1397 6 15.9489 6 15.75C6 15.5511 5.92098 15.3603 5.78033 15.2197C5.63968 15.079 5.44891 15 5.25 15H3.75ZM8.25 15C8.05109 15 7.86032 15.079 7.71967 15.2197C7.57902 15.3603 7.5 15.5511 7.5 15.75C7.5 15.9489 7.57902 16.1397 7.71967 16.2803C7.86032 16.421 8.05109 16.5 8.25 16.5H9.75C9.94891 16.5 10.1397 16.421 10.2803 16.2803C10.421 16.1397 10.5 15.9489 10.5 15.75C10.5 15.5511 10.421 15.3603 10.2803 15.2197C10.1397 15.079 9.94891 15 9.75 15H8.25ZM12.75 15C12.5511 15 12.3603 15.079 12.2197 15.2197C12.079 15.3603 12 15.5511 12 15.75C12 15.9489 12.079 16.1397 12.2197 16.2803C12.3603 16.421 12.5511 16.5 12.75 16.5H14.25C14.4489 16.5 14.6397 16.421 14.7803 16.2803C14.921 16.1397 15 15.9489 15 15.75C15 15.5511 14.921 15.3603 14.7803 15.2197C14.6397 15.079 14.4489 15 14.25 15H12.75ZM17.25 15C17.0511 15 16.8603 15.079 16.7197 15.2197C16.579 15.3603 16.5 15.5511 16.5 15.75C16.5 15.9489 16.579 16.1397 16.7197 16.2803C16.8603 16.421 17.0511 16.5 17.25 16.5H18.75C18.9489 16.5 19.1397 16.421 19.2803 16.2803C19.421 16.1397 19.5 15.9489 19.5 15.75C19.5 15.5511 19.421 15.3603 19.2803 15.2197C19.1397 15.079 18.9489 15 18.75 15H17.25Z" fill="#344F7A" />
                      </svg>
                    ),
                    name: 'CreditCard',
                    title: 'Credit Card',
                    content: (<Flex flexDirection={'column'} padding={'20px'}>
                      <Heading mb={4}>Credit Card</Heading>
                      {!viewMembershipDetail.card ? (
                        <Text textAlign={'center'} opacity={0.5}>No credit card found</Text>
                      ) : (
                        <UseCurrentCard card={viewMembershipDetail.card} />)}
                    </Flex>)
                  }, {
                    icon: (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19.5 3.5L18 2L16.5 3.5L15 2L13.5 3.5L12 2L10.5 3.5L9 2L7.5 3.5L6 2V16H3V19C3 20.66 4.34 22 6 22H18C19.66 22 21 20.66 21 19V2L19.5 3.5ZM15 20H6C5.45 20 5 19.55 5 19V18H15V20ZM19 19C19 19.55 18.55 20 18 20C17.45 20 17 19.55 17 19V16H8V5H19V19Z" fill="#344F7A" />
                        <path d="M9 7H15V9H9V7ZM9 10H15V12H9V10ZM16 7H18V9H16V7ZM16 10H18V12H16V10Z" fill="#344F7A" />
                      </svg>
                    ),
                    name: 'Receipts',
                    title: `Receipts (${(viewMembershipDetail.receipts || []).length})`,
                    content: <Flex flexDirection={'column'} padding={'20px'} width='100%'>
                      <Heading mb={4}>Receipts</Heading>
                      {viewMembershipDetail.receipts?.map(x => {
                        return (
                          <HoverableCard
                            key={`receipt${x.id}`}
                            hoveredId={''} id={`receipt${x.id}`} setHover={() => { }}
                            style={{
                              border: '1px solid #E2E2E2',
                              borderRadius: '10px',
                              marginBottom: '12px',
                              width: '100%',
                            }}>
                            <Flex style={{ width: '100%', padding: '12px 12px', minHeight: '70px' }} flexDirection={['column', 'row', 'row']}>
                              <Flex width='100%'>
                                <Box style={{ paddingLeft: '12px', alignSelf: 'center' }}>
                                  <Text style={{ color: 'rgba(52,79,122,0.6)', fontSize: '12px' }} mr={'12px'}>{x.id}</Text>
                                </Box>
                                <Box style={{ width: '1px', background: '#E2E2E2' }}></Box>
                                <Box style={{ paddingLeft: '12px', alignSelf: 'center' }}>
                                  <Text style={{ color: 'rgba(52,79,122,0.6)', fontSize: '12px' }} mr={'12px'}>{moment((x.date || 0) * 1000).format('MM/DD/YYYY')}</Text>
                                </Box>
                                <Box style={{ width: '1px', background: '#E2E2E2' }}></Box>
                                <Box style={{ paddingLeft: '12px', alignSelf: 'center', flex: 1, justifyContent: 'flex-end' }}>
                                  <Text mr={'12px'} style={{ fontSize: '12px' }} textAlign={'right'}>${(x.amount || 0) / 100}</Text>
                                </Box>
                                <Box style={{ width: '1px', background: '#E2E2E2' }} display={['none', 'initial', 'initial']}></Box>
                                <Box marginTop={['8px', 0, 0]} paddingLeft={[0, '12px', '12px']} style={{ alignSelf: 'center', cursor: 'pointer' }} onClick={() => window.open(x.link, '_blank')}>
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M19.5 3.5L18 2L16.5 3.5L15 2L13.5 3.5L12 2L10.5 3.5L9 2L7.5 3.5L6 2V16H3V19C3 20.66 4.34 22 6 22H18C19.66 22 21 20.66 21 19V2L19.5 3.5ZM15 20H6C5.45 20 5 19.55 5 19V18H15V20ZM19 19C19 19.55 18.55 20 18 20C17.45 20 17 19.55 17 19V16H8V5H19V19Z" fill="#344F7A" />
                                    <path d="M9 7H15V9H9V7ZM9 10H15V12H9V10ZM16 7H18V9H16V7ZM16 10H18V12H16V10Z" fill="#344F7A" />
                                  </svg>
                                </Box>
                              </Flex>
                            </Flex>
                          </HoverableCard>
                        )
                      })}
                    </Flex>
                  }]} />
              </Wrapper>
            </Box>
          </SpecialModalBackground>
        </Modal>
      )
      }
    </Flex >);
}